export const fontFamilies = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Source Sans Pro',
  'Noto Sans',
  'Poppins',
  'Fira Sans',
  'Arimo',
  // Serif
  'Merriweather',
  'Playfair Display',
  'Libre Baskerville',
  'Gelasio',
  'Tinos',
  'EB Garamond',
  // Monospace
  'JetBrains Mono',
  'Fira Code',
  'Inconsolata',
  'Source Code Pro',
  'Cousine',
  // Display / Headline
  'Oswald',
  'Raleway',
  'Bebas Neue',
  'Abril Fatface',
  'Anton',
  // Handwritten / Script
  'Pacifico',
  'Dancing Script',
  'Lobster',
  'Amatic SC',
  'Great Vibes',
] as const

export type FontFamily = (typeof fontFamilies)[number]

export const legacyFontFamilies = [
  'Helvetica Neue',
  'Georgia',
  'Times New Roman',
  'Courier New',
  'Arial',
  'Verdana',
  'Trebuchet MS',
  'Palatino Linotype',
  'Garamond',
] as const

export type LegacyFontFamily = (typeof legacyFontFamilies)[number]

/**
 * System font → bundled substitute, used by the html-renderer to register
 * each legacy name as an @font-face so PDFs render deterministically (the
 * render Lambda has no system fonts at all).
 *
 * Arimo/Tinos/Cousine/Gelasio are METRIC-COMPATIBLE with their system
 * counterparts (identical advance widths, verified by measurement), so
 * PDFs line-break exactly like the editor canvas showing the real system
 * font. The remaining faces have no open metric twin; their substitutes
 * are chosen narrower-or-equal so a mismatch loosens the layout instead
 * of wrapping/clipping text the canvas showed fitting.
 */
export const fontFamilyAliases: Record<LegacyFontFamily, FontFamily> = {
  // Metric-exact twins
  'Helvetica Neue': 'Arimo',
  Arial: 'Arimo',
  Georgia: 'Gelasio',
  'Times New Roman': 'Tinos',
  'Courier New': 'Cousine',
  // Best-available visual matches (no open metric twin exists)
  Verdana: 'Open Sans',
  'Trebuchet MS': 'Fira Sans',
  'Palatino Linotype': 'Gelasio',
  Garamond: 'EB Garamond',
}

export function canonicalizeFontFamily(
  value: string | null | undefined
): FontFamily | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const alias = fontFamilyAliases[trimmed as LegacyFontFamily]
  const candidate = alias ?? trimmed
  return fontFamilies.includes(candidate as FontFamily)
    ? (candidate as FontFamily)
    : undefined
}
